/* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing legacy code */
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import saveAs from "file-saver";
import { generateId, typeToString } from "utils/utils";
import WebRender from "../../../classes/WebRender";
import { StoryboardPdf } from "../StoryboardPdf/StoryboardPdf";

import { pdf } from "@react-pdf/renderer";

export type WebGlHandle = {
  createStoryboard: () => Promise<void>;
  goToStep: (step: number) => number;
  getTotalSteps: () => number;
};

const WebGl = forwardRef(({ responseData, height, width }: any, ref) => {
  const canvas = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<WebRender | null>(null);

  useEffect(() => {
    if (!canvas.current) return;
    const webRender = new WebRender(responseData, { width, height });
    webRender.setCollectStoryboard(false);
    webRender.render(canvas.current);
    instanceRef.current = webRender;

    return () => {
      instanceRef.current?.removeObjects();
      instanceRef.current = null;
    };
  }, []);

  const goToStep = (step: number) => {
    if (!canvas.current || !instanceRef.current) return 0;
    return instanceRef.current.renderAtStep(canvas.current, step);
  };

  const createStoryboard = async () => {
    const div = document.createElement("div");
    const storyCanvas = document.createElement("canvas");
    storyCanvas.width = 800;
    storyCanvas.height = 600;
    div.appendChild(storyCanvas);
    const webRender = new WebRender(responseData, { width, height });
    webRender.setCollectStoryboard(true);
    webRender.render(div);
    const name = typeToString(responseData.attr.type).toLocaleLowerCase().split(" ").join("-");
    const blob = await pdf(<StoryboardPdf images={webRender.storyBoardData} />).toBlob();
    saveAs(blob, `${name}-${generateId()}`);
    webRender.removeObjects();
    div.remove();
  };

  useImperativeHandle(
    ref,
    (): WebGlHandle => ({
      goToStep,
      getTotalSteps: () => instanceRef.current?.getTotalSteps() ?? responseData.spaces?.length ?? 0,
      createStoryboard,
    })
  );

  return <div className="canvas" ref={canvas} />;
});

export default React.memo(WebGl);
